import { AlertCircle } from "lucide-react"

import { cn } from "@/lib/utils"

import { AuthorSide, type Message } from "../data/review-data"

/*
   One message in the conversation: the message, then who and when beneath it,
   once — as the report's finding threads have it. No avatar and no name tag
   beside it: the wire names a side, not a person, so the side is the author.

   Own messages sit to the right. Bubbles are capped at ~62ch so lines stay
   readable even when the sheet is maximized to modal width. AI-authored
   messages get an info-tinted bubble and client ones a print-tinted one, so
   the reader always knows whether a sentence came from a person, which side,
   or a pipeline.

   Lives apart from the sheet because the sheet is already long and this is the
   piece most likely to keep growing: every new thing a message can be (sending,
   failed, edited, redacted) lands here rather than there.
*/

/** Who wrote it, as the report's finding threads say it. */
const SIDE_LABEL: Record<AuthorSide, string> = {
  [AuthorSide.CLIENT]: "Client",
  [AuthorSide.REVIEWER]: "Review team",
  [AuthorSide.AI]: "Pyxis AI",
}

const AT_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
})

function formatAt(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? "" : AT_FORMAT.format(date)
}

function formatDay(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  if (isSameDayDate(d, today)) return "Today"
  if (isSameDayDate(d, yesterday)) return "Yesterday"
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
}

function isSameDayDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function MessageBubble({
  message,
  showDayRule,
  onRetry,
}: {
  message: Message
  showDayRule: boolean
  /** Resend a message whose send failed. Absent where retrying is not offered. */
  onRetry?: (message: Message) => void
}): JSX.Element {
  const isAi = message.authorSide === AuthorSide.AI
  const sending = message.status === "sending"
  const failed = message.status === "failed"

  return (
    <>
      {showDayRule && (
        <li aria-hidden="true" className="flex items-center gap-2.5 py-1">
          <span className="h-px flex-1 bg-border-soft" />
          <span className="font-id text-[10px] uppercase tracking-[0.1em] text-text-3">
            {formatDay(message.at)}
          </span>
          <span className="h-px flex-1 bg-border-soft" />
        </li>
      )}
      <li className={cn("flex", message.mine ? "justify-end" : "justify-start")}>
        <div
          className={cn(
            "flex min-w-0 max-w-[min(62ch,86%)] flex-col",
            message.mine ? "items-end" : "items-start",
          )}
        >
          <div
            className={cn(
              "whitespace-pre-wrap break-words rounded-2xl px-4 py-3 text-body-sm leading-[1.6]",
              message.mine
                ? "bg-brand-tint text-text"
                : isAi
                  ? "bg-info-tint/60 text-text"
                  : message.authorSide === AuthorSide.CLIENT
                    ? "bg-print-tint/60 text-text"
                    : "bg-surface-sunken text-text",
              // Dimmed, never hidden: the text stays legible while it is on its
              // way, so a slow network looks slow rather than broken.
              sending && "opacity-60",
              failed && "ring-1 ring-inset ring-destructive/40",
            )}
          >
            {message.body}
          </div>
          {/* A time is a claim about when the server recorded something. While
              the request is in flight there is no such moment yet, so the slot
              says what is true instead of showing a timestamp that may shift. */}
          <span className="mt-1 px-1 text-[11px] text-text-3">
            {`${message.mine ? "You" : SIDE_LABEL[message.authorSide]} · ${
              sending ? "Sending…" : formatAt(message.at)
            }`}
          </span>
          {failed && (
            <FailureNotice
              mine={message.mine}
              message={message}
              {...(onRetry ? { onRetry } : {})}
            />
          )}
        </div>
      </li>
    </>
  )
}

/**
 * Why a message is still on screen but not yet in the record.
 *
 * Announced via `role="status"` rather than colour alone: a reviewer who
 * cannot distinguish the destructive ring still has to learn that what they
 * wrote was not saved. On a regulatory record a silently dropped sentence is
 * the worst outcome available, which is why the failed message keeps its text
 * and offers to resend rather than vanishing on rollback.
 */
function FailureNotice({
  mine,
  message,
  onRetry,
}: {
  mine: boolean
  message: Message
  onRetry?: (message: Message) => void
}): JSX.Element {
  return (
    <div
      role="status"
      className={cn(
        "flex items-center gap-1.5 px-0.5 text-caption text-destructive",
        mine && "flex-row-reverse",
      )}
    >
      <AlertCircle className="size-3.5 shrink-0" aria-hidden="true" />
      <span>Not sent</span>
      {onRetry && (
        <button
          type="button"
          onClick={() => {
            onRetry(message)
          }}
          className={cn(
            "rounded font-medium underline underline-offset-2",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
          )}
        >
          Retry
        </button>
      )}
    </div>
  )
}
